import { useCallback } from 'react';
import { useAuthStore } from '../../../../contexts/auth/useAuthStore';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUser } from '../../../../hooks/useUser';
import { toHttpsUrl } from '../../../../utils/urlUtils';
import type { Organization } from '../../../../types/organization.types';

// Webhook URLs for different stages
const WEBHOOK_URLS: Record<string, string> = {
  // Stage 3 - AI Assistant Setup (Twilio)
  'Stage 3 - Twilio Setup Complete': 'https://hooks.zapier.com/hooks/catch/12395623/uzdthcv/',

  // Stage 4 - IDX (third-party IDX Display URL); reuses the IDX Site Setup Complete Zap
  'Stage 4 - IDX Display URL': 'https://hooks.zapier.com/hooks/catch/12395623/uzj7i7n/',

  // Stage 4 - "Create Remi's IDX for me" (the original IDX setup steps)
  'Stage 4 - Step 1: MLS Licensing Agreement':
    'https://hooks.zapier.com/hooks/catch/12395623/uzkk41d/',
  'Stage 4 - IDX Domain Access Shared': 'https://hooks.zapier.com/hooks/catch/12395623/ufm5xan/',
  'Stage 4 - DNS Records Added Confirmation':
    'https://hooks.zapier.com/hooks/catch/12395623/uatne2w/',
  'Stage 4 - Step 2: IDX Site Setup Complete':
    'https://hooks.zapier.com/hooks/catch/12395623/uzj7i7n/',
  'Stage 4 - Step 2: IDX Site Setup (Skipped)':
    'https://hooks.zapier.com/hooks/catch/12395623/uzj7aa2/',

  // Stage 5 - CRM Integrations
  'Stage 5 - Integrations (CRM Setup)': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',

  // A CRM submitted through Speculo's Connect form (onboarding or Client Admin),
  // for the team to finish connecting; reuses the Integrations Zap
  'CRM Connect Request': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',

  // Revive Flow Tour - Completed or Skipped
  'Revive Tour - Completed': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',
  'Revive Tour - Skipped': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',

  // Onboarding Complete - Final step when user clicks "Create Your First Flow"
  'Onboarding Complete': 'https://hooks.zapier.com/hooks/catch/12395623/ufm843b/',
};

interface HubspotData {
  contact_id?: string;
  contact_url?: string;
  portal_id?: string;
}

interface WebhookPayload {
  stage: string;
  organizationName: string;
  organizationId: string;
  websiteUrl: string;
  hubspotContactUrl: string;
  dateTime: string;
  // User info
  userEmail: string;
  userFullName: string;
  userPhone: string;
  // Legacy field for backwards compatibility
  user: string;
  data: any;
}

export const useOnboardingWebhook = () => {
  const { user: authUser } = useAuthStore();
  const { user: firestoreUser } = useUser();
  const { organization } = useOrganizationStore();

  const sendWebhook = useCallback(
    async (stageName: string, data: any, orgOverride?: Organization | null) => {
      // Client Admin sends for the org being edited, not the admin's own org.
      const org = orgOverride ?? organization;
      try {
        // Get the appropriate webhook URL for this stage
        const webhookUrl = WEBHOOK_URLS[stageName];

        // Skip if no webhook URL configured for this stage
        if (!webhookUrl) {
          console.log('No webhook configured for stage:', stageName);
          return;
        }

        // Get HubSpot contact URL from organization data
        const hubspotData = org?.hubspot as HubspotData | undefined;

        // Website URL is the client's third-party IDX Display URL. The IDX stage sends its
        // webhook before the org is saved, so read it from the event data too.
        const idxDisplayUrl =
          org?.onboardingData?.idx?.idx_display_url || data?.idx_display_url;
        const websiteUrl = toHttpsUrl(idxDisplayUrl || org?.website || '');

        // Get user info - prefer Firestore user data (has phoneNumber), fallback to auth user
        const userEmail = firestoreUser?.email || authUser?.email || '';
        const userFullName =
          firestoreUser?.displayName ||
          (firestoreUser?.firstName && firestoreUser?.lastName
            ? `${firestoreUser.firstName} ${firestoreUser.lastName}`
            : firestoreUser?.firstName) ||
          (authUser as any)?.displayName ||
          '';
        const userPhone = firestoreUser?.phoneNumber || '';

        const webhookData: WebhookPayload = {
          stage: stageName,
          organizationName: data.org_name || org?.org_name || '',
          organizationId: org?.id || '',
          websiteUrl: websiteUrl,
          hubspotContactUrl: hubspotData?.contact_url || '',
          dateTime: new Date().toISOString(),
          // User info from Firestore user
          userEmail,
          userFullName,
          userPhone,
          // Legacy field for backwards compatibility
          user: userEmail,
          data: data,
        };

        await fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookData),
        });

        // Logged without the payload, which can hold CRM keys and logins.
        console.log('Webhook sent:', stageName);
      } catch (error) {
        console.error('Webhook error:', error);
        // Don't block submission on webhook failure
      }
    },
    [authUser, firestoreUser, organization]
  );

  return { sendWebhook };
};
